import { getRelativeDateText } from '@/utils/dates/get-relative-date-text';
import { sortByDate } from '@/utils/tables/sort-by-date';
import { Currency } from '@seapoint/server/enums';
import { createColumnHelper } from '@tanstack/react-table';
import { AvatarNameDescription } from '../../../../design-system/avatar/avatar-name-description/avatar-name-description';
import { Badge } from '../../../../design-system/badge/badge';
import { ColumnHeaderWithSorting } from '../../../../design-system/data-table/column-header-with-sorting';
import { InvoiceEvidenceView } from '../../../../types/entity-types';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { getNormalizedAmount } from '../../../../utils/get-normalized-amount';
import { isSupportedCurrency } from '../../../payments/utils/is-supported-currency';
import { getPaidInvoiceRowDate } from '../../utils/get-paid-invoice-row-data';
import { InvoiceDrawerTrigger } from '../invoice-drawer/invoice-drawer-trigger';
import { TransactionDrawerTrigger } from '../invoice-drawer/transaction-drawer-trigger';
import { getInvoiceDateCellContent } from './to-pay-invoice-table-columns-definition';
import { getJointAvatarDescription } from './utils/get-joint-avatar-description';
import { getPaidInvoiceMatchingStatus } from './utils/get-paid-invoice-matching-status';
import { getPaidInvoiceMatchingStatusBadgeVariant } from './utils/get-paid-invoice-matching-status-badge-variant';

export const DATE_PAID_COLUMN_ID = 'paidDate';
export const INVOICE_INFO_COLUMN_ID = 'invoiceInfo';

const columnHelper = createColumnHelper<InvoiceEvidenceView>();

export const paidInvoiceTableColumnsDefinition = [
  columnHelper.accessor((row) => row.transaction?.fspDate ?? 'Unknown', {
    id: DATE_PAID_COLUMN_ID,
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Date Paid" />,
    cell: ({ row }) => {
      const rowDate = getPaidInvoiceRowDate({
        fspDate: row.original.transaction?.fspDate,
        dueDate: row.original.billingDates?.dueDate,
      });

      const cellContent = getRelativeDateText(rowDate);

      return <p className="text-sm leading-6 font-normal tracking-tight text-black">{cellContent}</p>;
    },
    sortingFn: sortByDate,
    size: 12,
    minSize: 12,
  }),
  //
  // TODO: need to add the due date in the description of the avatar component
  // https://seapointfinance.slack.com/archives/D087F33BMUN/p1753800231636159
  //
  columnHelper.accessor(
    (row) => row.suggestedBeneficiary?.name ?? row.vendorInformation?.vendorCompanyName ?? 'Unknown',
    {
      id: INVOICE_INFO_COLUMN_ID,
      header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Vendor" />,
      cell: ({ row }) => {
        const cellContent =
          row.original.suggestedBeneficiary?.name ?? row.original.vendorInformation?.vendorCompanyName ?? 'Unknown';

        const description = getJointAvatarDescription({
          item1: getInvoiceDateCellContent(
            row.original.billingDates?.dueDate ?? row.original.billingDates?.invoiceDate,
          ),
          item2: row.original.invoiceDetails?.invoiceReference,
        });

        return <AvatarNameDescription avatar={{ showAvatar: true }} name={cellContent} description={description} />;
      },
      size: 25,
    },
  ),
  columnHelper.accessor((row) => row.transaction?.account, {
    id: 'account',
    header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Account" />,
    cell: ({ row }) => {
      // If no transaction or account, show plain "Unknown" text without avatar
      if (!row.original.transaction?.account) {
        return <p className="text-sm leading-6 font-normal tracking-tight text-black">Unknown</p>;
      }

      const { name, logo } = {
        name: row.original.transaction.account.institution?.name,
        logo: row.original.transaction.account.institution?.logo,
      };

      const accountNameWithCurrency = name ? `${name} (${row.original.transaction.account.currency})` : 'Unknown';

      return (
        <AvatarNameDescription
          avatar={{ showAvatar: true, avatarUrl: logo, size: 'xs' }}
          name={accountNameWithCurrency}
        />
      );
    },
    size: 20,
  }),
  columnHelper.accessor(
    (row) => getPaidInvoiceMatchingStatus(row.isPaidUpdatedByType, !!row.transactionId, !!row.transaction),
    {
      id: 'status',
      header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Status" />,
      cell: ({ row }) => {
        const status = getPaidInvoiceMatchingStatus(
          row.original.isPaidUpdatedByType,
          !!row.original.transactionId,
          !!row.original.transaction,
        );

        // Show plain text for "No matched transaction", otherwise show badge
        if (status === 'No matched transaction') {
          return <p className="text-sm leading-6 font-normal tracking-tight text-black">{status}</p>;
        }

        const badgeVariant = getPaidInvoiceMatchingStatusBadgeVariant(status);

        return (
          <Badge variant={badgeVariant} shape="pill">
            {status}
          </Badge>
        );
      },
      size: 18,
      minSize: 18,
    },
  ),
  columnHelper.accessor(
    (row) => {
      if (
        !row.invoiceDetails?.totalAmount ||
        !row.invoiceDetails?.currency ||
        !isSupportedCurrency(row.invoiceDetails?.currency)
      ) {
        return '—';
      }

      return getNormalizedAmount({
        amount: row.invoiceDetails.totalAmount,
        isCredit: true,
      });
    },
    {
      id: 'amount',
      header: ({ column }) => <ColumnHeaderWithSorting column={column} label="Amount" />,
      cell: ({ row }) => {
        // Check if transaction has valid amount and currency
        const hasTransactionAmount =
          row.original.transaction?.amount &&
          row.original.transaction?.currency &&
          isSupportedCurrency(row.original.transaction?.currency);

        // Check if invoice has valid amount and currency
        const hasInvoiceAmount =
          row.original.invoiceDetails?.totalAmount &&
          row.original.invoiceDetails?.currency &&
          isSupportedCurrency(row.original.invoiceDetails?.currency);

        // If neither source has valid data, show placeholder
        if (!hasTransactionAmount && !hasInvoiceAmount) {
          return <p className="text-end leading-6 font-normal tracking-tight text-black">—</p>;
        }

        // Prefer transaction data (actual payment), fallback to invoice data
        const amount = hasTransactionAmount
          ? row.original.transaction!.amount
          : row.original.invoiceDetails!.totalAmount!;

        const currency = hasTransactionAmount
          ? row.original.transaction!.currency
          : row.original.invoiceDetails!.currency!;

        // Format the amount with currency (currency already validated by isSupportedCurrency above)
        const amountValue = getAmountWithCurrency({
          amount,
          currency: currency as Currency, // Type-safe cast - validated by isSupportedCurrency above
          isAccountBalance: true,
        });

        return <p className="text-end text-sm leading-6 font-normal tracking-tight text-black">{amountValue}</p>;
      },
      size: 15,
      minSize: 15,
    },
  ),
  columnHelper.display({
    id: 'reviewButton',
    cell: ({ row }) => {
      // Show transaction drawer only if we have both transactionId AND the transaction object
      // If transaction is soft-deleted, transaction object will be undefined, so show invoice drawer instead
      if (row.original.transactionId && row.original.transaction) {
        return <TransactionDrawerTrigger rowEvidence={row.original}>View</TransactionDrawerTrigger>;
      }

      return <InvoiceDrawerTrigger rowEvidence={row.original}>View</InvoiceDrawerTrigger>;
    },
    size: 10,
    minSize: 10,
  }),
];
